/**
 * The case's closing choreography, taken from the hand-keyed curves of the
 * reference (annnguyentt/vibe-coding `cd-case`, `CLOSE` in CaseStage.js).
 *
 * A spring can't make that close: the lid FLICKS most of the way (a third of
 * its travel in the first eighth of the time), drifts shut, lands, pops back
 * open 4° and settles — while the pair slides home late and the whole case
 * dips once ("thunk") as the lid lands. Each curve is a monotone cubic through
 * the reference's keys (no overshoot between keys), sampled into keyframes
 * that motion plays linearly.
 *
 * Reference time runs 1.95 s; UI time is scaled to ~1.2 s.
 */

type Key = readonly [time: number, value: number];

const TIME_SCALE = 0.62;
const REFERENCE_CLOSE_T = 1.95;
/** Seconds for the whole close, in UI time. */
export const CLOSE_DURATION = REFERENCE_CLOSE_T * TIME_SCALE;
/** When the lid first touches down (reference 1.7 s), as a fraction of it. */
export const CLOSE_LAND_AT = 1.7 / REFERENCE_CLOSE_T;
/** When the pose may start drifting back to rest (reference 0.9 s). */
export const CLOSE_POSE_DELAY = 0.9 * TIME_SCALE;

const OPEN_ANGLE_RAD = 3.05;

// Lid angle in radians, 3.05 = open flat.
const LID: Key[] = [
  [0, OPEN_ANGLE_RAD],
  [0.22, 1.75],
  [0.48, 1.55],
  [1.05, 1.08],
  [1.42, 0.6],
  [1.6, 0.26],
  [1.7, 0],
  [1.78, 0.07],
  [1.88, 0],
  [1.95, 0],
];
// 1 = spread open (slid right, shrunk), 0 = home.
const SPREAD: Key[] = [
  [0, 1],
  [0.3, 0.92],
  [1.2, 0.55],
  [1.72, 0],
  [1.95, 0],
];
// Extra pitch (radians) on top of the pose: the landing thunk.
const THUNK: Key[] = [
  [0, 0],
  [1.6, 0],
  [1.7, 0.045],
  [1.82, -0.012],
  [1.95, 0],
];
// Roll (radians): the case leans a touch as the lid comes over.
const ROLL: Key[] = [
  [0, 0],
  [0.8, -0.05],
  [1.95, 0],
];

/** Fritsch–Carlson monotone cubic Hermite through `keys`. */
export function monotoneCurve(keys: readonly Key[]): (t: number) => number {
  const n = keys.length;
  const xs = keys.map((k) => k[0]);
  const ys = keys.map((k) => k[1]);
  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  }
  const m: number[] = new Array(n).fill(0);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      m[i] = tau * a * d[i];
      m[i + 1] = tau * b * d[i];
    }
  }
  return (t: number) => {
    if (t <= xs[0]) return ys[0];
    if (t >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (i < n - 2 && t > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const u = (t - xs[i]) / h;
    const u2 = u * u;
    const u3 = u2 * u;
    return (
      (2 * u3 - 3 * u2 + 1) * ys[i] +
      (u3 - 2 * u2 + u) * h * m[i] +
      (-2 * u3 + 3 * u2) * ys[i + 1] +
      (u3 - u2) * h * m[i + 1]
    );
  };
}

const SAMPLES = 40;

function sample(keys: readonly Key[], map: (value: number) => number) {
  const curve = monotoneCurve(keys);
  const values: number[] = [];
  const times: number[] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const p = i / SAMPLES;
    times.push(p);
    values.push(map(curve(p * REFERENCE_CLOSE_T)));
  }
  return { values, times };
}

const RAD = 180 / Math.PI;

/**
 * Keyframes for one close. `openLidDeg` is the case's open lid angle (−176);
 * `openX`/`openScale` are where the spread pair currently sits.
 */
export function closeKeyframes({
  openLidDeg,
  openX,
  openScale,
}: {
  openLidDeg: number;
  openX: number;
  openScale: number;
}) {
  const lid = sample(LID, (rad) => (rad / OPEN_ANGLE_RAD) * openLidDeg);
  const x = sample(SPREAD, (s) => s * openX);
  const scale = sample(SPREAD, (s) => 1 + (openScale - 1) * s);
  const thunk = sample(THUNK, (rad) => rad * RAD);
  const roll = sample(ROLL, (rad) => rad * RAD);
  return { lid, x, scale, thunk, roll };
}
